import { CoachingPracticeAudio } from '@euler/model/coaching-practice';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { Audio, AVPlaybackStatus } from 'expo-av';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import { clipUri } from '../voice/clipUri';

export interface ClipPlayer {
  /** A clip is playing. */
  playing$: BehaviorSubject<boolean>;
  /** The turn of the clip playing now. */
  playingTurn$: BehaviorSubject<number | null>;
  /**
   * How much of a clip has been heard, 0 to 1; a clip skipped or cut short
   * by an error counts as heard, so its words are not held back.
   */
  progress$: Subject<ClipProgress>;
  /** Plays after what is queued; a clip without audio is skipped. */
  enqueue: (clips: CoachingPracticeAudio[]) => void;
  /** Stops now and drops the queue: muted, or the trainee talks over it. */
  interrupt: () => void;
}

export interface ClipProgress {
  clip: CoachingPracticeAudio;
  fraction: number;
}

/** A clip stopped this long without finishing has stalled. */
const kStallMs = 3_000;

/** The customer's voice, sentence by sentence, while not muted. */
export function useClipPlayer(speak$: BehaviorSubject<boolean>): ClipPlayer {
  const playing$ = useCreation(() => new BehaviorSubject(false), []);
  const playingTurn$ = useCreation(
    () => new BehaviorSubject<number | null>(null),
    [],
  );
  const progress$ = useCreation(() => new Subject<ClipProgress>(), []);
  const queue = useRef<CoachingPracticeAudio[]>([]);
  const current = useRef<{ sound: Audio.Sound; dispose: () => void } | null>(
    null,
  );
  // A clip is loading or playing.
  const busy = useRef(false);
  // Bumped on interrupt: a clip still loading belongs to a dropped queue.
  const generation = useRef(0);
  const unmounted = useRef(false);

  const release = useCallback(() => {
    const playing = current.current;
    current.current = null;
    if (!playing) return;
    void playing.sound
      .unloadAsync()
      .catch(error => console.error('coaching clip unload failed', error))
      .finally(playing.dispose);
  }, []);

  const next = useCallback(async () => {
    if (busy.current || unmounted.current) return;
    const clip = queue.current.shift();
    if (!clip) {
      playing$.next(false);
      playingTurn$.next(null);
      return;
    }
    if (!clip.audio || !speak$.getValue()) {
      progress$.next({ clip, fraction: 1 });
      void next();
      return;
    }
    busy.current = true;
    const mine = generation.current;
    let ended = false;
    // A clip the system stopped (an audio interruption) never reports an
    // end: one that has not moved for a while counts as heard, or the scene
    // would wait on it for good.
    let stall: ReturnType<typeof setTimeout> | null = null;
    const watch = () => {
      if (stall) clearTimeout(stall);
      stall = setTimeout(() => {
        console.error('coaching clip stalled; moving on');
        finished();
      }, kStallMs);
    };
    const finished = () => {
      if (stall) clearTimeout(stall);
      stall = null;
      if (ended || mine !== generation.current) return;
      ended = true;
      progress$.next({ clip, fraction: 1 });
      release();
      busy.current = false;
      void next();
    };
    playing$.next(true);
    playingTurn$.next(clip.turn);
    try {
      const file = await clipUri(
        `${clip.turn}-${clip.seq}`,
        clip.mime,
        clip.audio,
      );
      if (mine !== generation.current || unmounted.current) {
        file.dispose();
        return;
      }
      const { sound } = await Audio.Sound.createAsync(
        { uri: file.uri },
        { shouldPlay: true, progressUpdateIntervalMillis: 100 },
        (status: AVPlaybackStatus) => {
          if (status.isLoaded && status.isPlaying && !ended) watch();
          if (
            status.isLoaded &&
            status.durationMillis &&
            !ended &&
            mine === generation.current
          )
            progress$.next({
              clip,
              fraction: Math.min(
                1,
                status.positionMillis / status.durationMillis,
              ),
            });
          // A clip that stops on an error moves the queue on like one that
          // finished; otherwise nothing after it would play.
          if (status.isLoaded ? status.didJustFinish : !!status.error) {
            if (status.isLoaded === false)
              console.error('coaching clip playback failed', status.error);
            finished();
          }
        },
      );
      if (mine !== generation.current || unmounted.current || ended) {
        // Dropped, or so short it finished while loading.
        void sound.unloadAsync().finally(file.dispose);
        return;
      }
      current.current = { sound, dispose: file.dispose };
      // Never started at all is a stall too.
      watch();
    } catch (error) {
      console.error('coaching clip playback failed', error);
      finished();
    }
  }, [playing$, playingTurn$, progress$, release, speak$]);

  const interrupt = useCallback(() => {
    generation.current += 1;
    queue.current = [];
    busy.current = false;
    release();
    playing$.next(false);
    playingTurn$.next(null);
  }, [playing$, playingTurn$, release]);

  const enqueue = useCallback(
    (clips: CoachingPracticeAudio[]) => {
      if (!clips.length) return;
      queue.current.push(...clips);
      void next();
    },
    [next],
  );

  useEffect(() => {
    Audio.setAudioModeAsync({ playsInSilentModeIOS: true }).catch(error =>
      console.error('coaching audio mode failed', error),
    );
    const muted = speak$.subscribe(speak => {
      if (!speak) interrupt();
    });
    return () => {
      unmounted.current = true;
      muted.unsubscribe();
      interrupt();
    };
  }, [interrupt, speak$]);

  return useMemo(
    () => ({ playing$, playingTurn$, progress$, enqueue, interrupt }),
    [playing$, playingTurn$, progress$, enqueue, interrupt],
  );
}
